import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, X, Megaphone, Lock } from 'lucide-react';
import { heroSlides, newsEvents, researchProjects, perspectives, exploreOptions } from '../mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { isApplicationsClosed } from '../lib/summerSchoolStatus';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const summerSchoolClosed = isApplicationsClosed();
  const [showSummerSchoolBanner, setShowSummerSchoolBanner] = useState(() => {
    if (isApplicationsClosed()) return false;
    try {
      return sessionStorage.getItem('ss2026_banner_dismissed') !== '1';
    } catch {
      return true;
    }
  });

  const dismissBanner = () => {
    setShowSummerSchoolBanner(false);
    try {
      sessionStorage.setItem('ss2026_banner_dismissed', '1');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div className="min-h-screen">
      {/* Floating Summer School Announcement Banner */}
      {showSummerSchoolBanner && (
        <div
          className="relative z-20 bg-gradient-to-r from-green-800 via-emerald-700 to-green-800 text-white shadow-md border-b border-green-900/40 animate-fade-in"
          data-testid="summer-school-announcement-banner"
        >
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center md:text-left">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 flex-shrink-0">
                <Megaphone className="w-4 h-4 text-emerald-100" />
              </span>

              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full bg-white text-green-900 text-xs font-bold uppercase tracking-wider">
                Applications Open
              </span>

              <span className="text-sm md:text-base font-medium">
                <span className="font-bold">2026 SAS Summer School</span>
                <span className="hidden md:inline"> — An intensive research training programme</span>
                <span className="mx-2 text-emerald-200">·</span>
                <span className="text-emerald-50">Deadline: <span className="font-bold text-white">27 May 2026</span></span>
              </span>

              <Link
                to="/summer-school"
                className="inline-flex items-center gap-1.5 bg-white text-green-900 hover:bg-emerald-50 font-semibold text-sm px-4 py-1.5 rounded-full shadow-sm transition-colors"
                data-testid="summer-school-banner-apply-btn"
              >
                Apply Now
                <ArrowRight className="w-4 h-4" />
              </Link>

              <button
                onClick={dismissBanner}
                className="ml-auto md:ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/15 transition-colors flex-shrink-0"
                aria-label="Dismiss announcement"
                data-testid="summer-school-banner-close-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Slider */}
      <section className="relative h-[600px] overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
            </div>
            <div className="relative h-full flex items-center">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl text-white">
                  <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
                    {slide.title}
                  </h1>
                  <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                    {slide.subtitle}
                  </p>
                  <Link to="/about">
                    <Button className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg">
                      Learn More
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm p-3 rounded-full transition-all z-10"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm p-3 rounded-full transition-all z-10"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-green-500 w-8' : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </section>

      {/* News & Events Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">News & Events</h2>
              <div className="h-1 w-24 bg-green-600"></div>
            </div>
            <Link to="/events">
              <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                View All Events
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* Three Blocks Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Block 1: Highland Institute in the News */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  Highland Institute in the News
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Recent media coverage featuring our research, projects, and public engagements
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* News image */}
                <div className="mb-6 overflow-hidden rounded-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&h=400&fit=crop" 
                    alt="News and media coverage"
                    className="w-full h-48 object-cover"
                  />
                </div>

                {/* News articles list */}
                <div className="space-y-4">
                  <div className="border-l-4 border-green-600 pl-4">
                    <a 
                      href="https://www.morungexpress.com/report-links-indigenous-knowledge-to-climate-resilience-in-eastern-nagaland"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                    >
                      <h4 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors mb-1 leading-tight">
                        Report links indigenous knowledge to climate resilience in eastern Nagaland
                      </h4>
                      <p className="text-sm text-gray-500 italic mb-2">The Morung Express</p>
                    </a>
                  </div>

                  <div className="border-l-4 border-green-600 pl-4">
                    <a 
                      href="https://www.easternmirrornagaland.com/report-highlights-need-for-sustainable-year-round-tourism-strategy-in-kohima-beyond-hornbill-festival"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                    >
                      <h4 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors mb-1 leading-tight">
                        Report highlights need for sustainable year-round tourism strategy in Kohima beyond Hornbill Festival
                      </h4>
                      <p className="text-sm text-gray-500 italic mb-2">Eastern Mirror Nagaland</p>
                    </a>
                  </div>

                  <div className="border-l-4 border-green-600 pl-4">
                    <a 
                      href="https://mokokchungtimes.com/study-finds-kohimas-tourism-is-too-dependent-on-hornbill-festival/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                    >
                      <h4 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors mb-1 leading-tight">
                        Study finds Kohima's tourism is too dependent on Hornbill Festival
                      </h4>
                      <p className="text-sm text-gray-500 italic mb-2">Mokokchung Times</p>
                    </a>
                  </div>
                </div>

                {/* CTA Link */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Link 
                    to="/urbaltour"
                    className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                  >
                    Learn more about the Urbaltour Project
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Block 2: 2026 SAS Summer School */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  2026 SAS Summer School Concludes
                </CardTitle>
                <CardDescription className="text-gray-600">
                  THI congratulates the successful participants
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Candidates Photo */}
                <div className="mb-6 overflow-hidden rounded-lg">
                  <img 
                    src="https://customer-assets-0z36b82j.emergentagent.net/job_7a4f11f8-6c06-4e1c-b433-73d9453c195e/artifacts/vmucx7fx_WhatsApp%20Image%202026-08-29%20at%207.55.42%20PM.jpeg" 
                    alt="2026 SAS Summer School successful participants with the Research Director and Programme Coordinator"
                    className="w-full h-48 object-cover object-top"
                  />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <div className="border-l-4 border-green-700 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 leading-tight">
                      Congratulations to Our 2026 Summer School Participants
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      The Highland Institute is proud to congratulate all participants who successfully completed 
                      the 2026 SAS Summer School — an intensive training programme for final-year master's students 
                      and recent graduates preparing for advanced academic and research pathways.
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-2">
                      The Summer School serves as the initial phase of the Highland Institute's 12-month 
                      Postgraduate Certificate in Research (PGCertR).
                    </p>
                    <p className="text-sm font-semibold text-green-700">
                      Programme completed: 15 June – 31 August 2026
                    </p>
                  </div>
                </div>

                {/* CTA Link */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Link 
                    to="/summer-school"
                    className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                  >
                    View Programme Details
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Block 3: HAGAF Project */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  HAGAF
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Histories of Asia's Gendered Armed Frontiers, 1780–2021
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Image - HAGAF Logo */}
                <div className="mb-6 overflow-hidden rounded-lg bg-white flex items-center justify-center p-4">
                  <img 
                    src="https://customer-assets-0z36b82j.emergentagent.net/job_7a4f11f8-6c06-4e1c-b433-73d9453c195e/artifacts/ks29hq79_HAGAF%20LOGO.jpg" 
                    alt="HAGAF - Histories of Asia's Gendered Armed Frontiers"
                    className="w-full h-40 object-contain"
                  />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <div className="border-l-4 border-indigo-600 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2 leading-tight">
                      What happens to everyday life, family relationships and community memories when war and militarisation become part of the landscape?
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      These questions lie at the heart of <strong>Histories of Asia's Gendered Armed Frontiers, 1780–2021 (HAGAF)</strong>, a five-year international research initiative (2025–2030) led by Professor Mandy Sadan at the University of Warwick, with The Highland Institute among its partners.
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-2">
                      Funded by the European Research Council through the EU's Horizon Europe programme, HAGAF explores communities in Bangladesh, India and Myanmar through historical and ethnographic research.
                    </p>
                    <p className="text-sm font-semibold text-indigo-700">
                      A five-year ERC-funded international research initiative
                    </p>
                  </div>
                </div>

                {/* CTA Link */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Link 
                    to="/research/hagaf"
                    className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Learn More About HAGAF
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Research Projects */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">Research Projects</h2>
              <div className="h-1 w-24 bg-green-600"></div>
            </div>
            <Link to="/research">
              <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                View All Research
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {researchProjects.map((project) => (
              <Link key={project.id} to={`/research/${project.slug}`}>
                <Card className="group hover:shadow-xl transition-all duration-300 h-full">
                  <div className="flex flex-col md:flex-row h-full">
                    <div className="md:w-1/3 relative overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-64 md:h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="md:w-2/3 p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-green-600 font-medium mb-4">{project.subtitle}</p>
                      <p className="text-gray-600 mb-4 line-clamp-3">{project.description}</p>
                      <span className="inline-flex items-center text-green-600 hover:text-green-700 font-medium">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Perspectives */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Perspectives</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {perspectives.map((item) => (
              <a
                key={item.id}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-2xl font-bold mb-2 group-hover:text-green-400 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-200">{item.description}</p>
                    </div>
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Explore Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Explore</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {exploreOptions.map((option) => (
              <Card key={option.id} className="group hover:shadow-xl transition-all duration-300">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={option.image}
                    alt={option.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold">{option.title}</h3>
                  </div>
                </div>
                <CardHeader>
                  <CardDescription className="text-green-600 font-medium">
                    {option.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{option.description}</p>
                  <ul className="space-y-2">
                    {option.links.slice(0, 3).map((link, idx) => (
                      <li key={idx}>
                        <Link
                          to={link.url}
                          className="text-sm text-gray-700 hover:text-green-600 transition-colors flex items-center"
                        >
                          <ArrowRight className="w-3 h-3 mr-2" />
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/${option.slug}`}>
                    <Button className="mt-4 w-full bg-green-600 hover:bg-green-700">
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join Our Community</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Be part of a vibrant community dedicated to sustainable futures and indigenous knowledge systems.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/study-with-us">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Study With Us
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;